import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ArrowsClockwise, Bug, Trash, Warning, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { api, formatErr } from "@/lib/api";

const STATUS_FILTERS = [
  { label: "All", value: "" },
  { label: "500", value: "500" },
  { label: "502", value: "502" },
  { label: "503", value: "503" },
];

export default function ErrorLogsAdmin() {
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState({ total: 0, last_24h: 0 });
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const q = statusFilter ? `?status=${statusFilter}` : "";
      const [list, st] = await Promise.all([
        api.get(`/admin/errors${q}`),
        api.get("/admin/errors/stats"),
      ]);
      setRows(list.data);
      setStats(st.data);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const onDelete = async (errorId) => {
    try {
      await api.delete(`/admin/errors/${errorId}`);
      setRows((r) => r.filter((x) => x.error_id !== errorId));
      toast.success("Removed");
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    }
  };

  const onClearAll = async () => {
    setClearing(true);
    try {
      const { data } = await api.post("/admin/errors/clear");
      toast.success(`Cleared ${data.deleted} entries`);
      setRows([]);
      setStats({ total: 0, last_24h: 0 });
      setConfirmClear(false);
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail) || e.message);
    } finally {
      setClearing(false);
    }
  };

  const openRow = useMemo(() => rows.find((r) => r.error_id === openId), [rows, openId]);

  return (
    <AppShell>
      <div className="mb-8">
        <Link to="/admin" className="text-xs font-mono text-[--muted] hover:text-[--ink] inline-flex items-center gap-1" data-testid="errors-back-link">
          <ArrowLeft weight="bold" size={12} /> Admin
        </Link>
        <div className="mt-2 overline text-[--muted] inline-flex items-center gap-2">
          <Bug weight="bold" size={14} /> ERROR LOG
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter mt-2">Backend errors</h1>
        <p className="text-[--muted] mt-3 max-w-2xl text-sm">
          Every unhandled exception and 5xx response is logged here with a full traceback. End-users
          only see a friendly generic message plus the reference id — share that id when triaging.
        </p>
      </div>

      {/* Stats / actions row */}
      <section className="hard-border bg-white p-6 mb-8" data-testid="errors-stats-card">
        <div className="flex flex-wrap items-end gap-6 justify-between">
          <div className="flex gap-8">
            <div>
              <div className="overline text-[--muted]">TOTAL</div>
              <div className="font-display font-black text-3xl tabular-nums" data-testid="errors-total">
                {stats.total.toLocaleString()}
              </div>
            </div>
            <div>
              <div className="overline text-[--muted]">LAST 24H</div>
              <div className="font-display font-black text-3xl tabular-nums" data-testid="errors-last-24h">
                {stats.last_24h.toLocaleString()}
              </div>
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="overline text-[--muted] mr-2">STATUS</div>
            {STATUS_FILTERS.map((f) => (
              <button
                key={f.value}
                onClick={() => setStatusFilter(f.value)}
                data-testid={`errors-filter-${f.value || "all"}`}
                className={`px-3 py-1.5 text-xs font-bold border border-[--ink] transition-colors ${
                  statusFilter === f.value ? "bg-[--ink] text-white" : "bg-white hover:bg-[--paper]"
                }`}
              >
                {f.label}
              </button>
            ))}
            <button
              onClick={load}
              data-testid="errors-refresh-btn"
              className="ml-2 border border-[--ink] px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-[--paper]"
              title="Reload"
            >
              <ArrowsClockwise weight="bold" size={12} /> Refresh
            </button>
            <button
              onClick={() => setConfirmClear(true)}
              disabled={rows.length === 0}
              data-testid="errors-clear-all-btn"
              className="border border-red-600 text-red-700 px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1 hover:bg-red-50 disabled:opacity-40"
              title="Wipe all logged errors"
            >
              <Trash weight="bold" size={12} /> Clear all
            </button>
          </div>
        </div>
      </section>

      {/* Table */}
      <section className="hard-border bg-white mb-12" data-testid="errors-table">
        <div className="grid grid-cols-12 gap-3 px-4 py-3 border-b border-[--line] text-[10px] font-mono text-[--muted] uppercase tracking-wider">
          <div className="col-span-2">When</div>
          <div className="col-span-1">Status</div>
          <div className="col-span-2">Type</div>
          <div className="col-span-3">Path</div>
          <div className="col-span-2">User</div>
          <div className="col-span-2 text-right">Ref / actions</div>
        </div>
        {loading ? (
          <div className="p-6 text-xs text-[--muted] font-mono">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-8 text-center text-sm text-[--muted] font-mono" data-testid="errors-empty">
            No errors recorded. <span className="opacity-60">(That's a good thing.)</span>
          </div>
        ) : rows.map((r) => (
          <button
            key={r.error_id}
            onClick={() => setOpenId(r.error_id)}
            data-testid={`error-row-${r.error_id}`}
            className="w-full grid grid-cols-12 gap-3 px-4 py-3 text-xs text-left border-b border-[--line] hover:bg-[--paper] transition-colors items-center"
          >
            <div className="col-span-2 font-mono text-[--muted]">
              {new Date(r.ts).toLocaleString()}
            </div>
            <div className="col-span-1">
              <span className={`inline-flex items-center px-2 py-0.5 text-[11px] font-bold font-mono ${
                r.status_code >= 500 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
              }`}>
                {r.status_code}
              </span>
            </div>
            <div className="col-span-2 font-mono font-bold truncate" title={r.error_type}>{r.error_type}</div>
            <div className="col-span-3 font-mono text-[--muted] truncate" title={`${r.method} ${r.path}`}>
              <span className="font-bold text-[--ink]">{r.method}</span> {r.path}
            </div>
            <div className="col-span-2 font-mono text-[--muted] truncate" title={r.user_email || r.user_id || ""}>
              {r.user_email || r.user_id || <span className="opacity-50">anon</span>}
            </div>
            <div className="col-span-2 flex items-center justify-end gap-2">
              <code className="font-mono text-[10px] text-[--muted]">{r.error_id}</code>
              <span
                role="button"
                tabIndex={0}
                onClick={(e) => { e.stopPropagation(); onDelete(r.error_id); }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" || e.key === " ") {
                    e.stopPropagation();
                    onDelete(r.error_id);
                  }
                }}
                data-testid={`error-delete-${r.error_id}`}
                className="border border-[--line] p-1 hover:bg-red-50 hover:border-red-600 cursor-pointer"
                title="Delete"
              >
                <Trash size={12} />
              </span>
            </div>
          </button>
        ))}
      </section>

      {openRow && (
        <ErrorDetailModal row={openRow} onClose={() => setOpenId(null)} />
      )}

      {confirmClear && (
        <ConfirmClearModal
          busy={clearing}
          onCancel={() => setConfirmClear(false)}
          onConfirm={onClearAll}
        />
      )}
    </AppShell>
  );
}

function ErrorDetailModal({ row, onClose }) {
  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onClose}>
      <div
        className="bg-white hard-border max-w-3xl w-full max-h-[85vh] overflow-y-auto p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="error-detail-modal"
      >
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="overline text-[--muted] mb-1">ERROR DETAIL</div>
            <h2 className="font-display font-black text-2xl tracking-tighter inline-flex items-center gap-2">
              <Warning weight="duotone" size={28} className="text-red-600" /> {row.error_type}
            </h2>
            <div className="text-xs font-mono text-[--muted] mt-1">
              ref <span className="text-[--ink] font-bold">{row.error_id}</span> · {new Date(row.ts).toLocaleString()}
            </div>
          </div>
          <button onClick={onClose} className="border border-[--ink] p-1.5 hover:bg-[--paper]" data-testid="error-detail-close">
            <X weight="bold" size={14} />
          </button>
        </div>

        <Field label="Status">
          <span className={`inline-flex items-center px-2 py-0.5 text-xs font-bold font-mono ${
            row.status_code >= 500 ? "bg-red-100 text-red-800" : "bg-amber-100 text-amber-800"
          }`}>{row.status_code}</span>
        </Field>
        <Field label="Endpoint">
          <code className="font-mono text-xs">{row.method} {row.path}{row.query ? `?${row.query}` : ""}</code>
        </Field>
        <Field label="User">
          <code className="font-mono text-xs">{row.user_email || row.user_id || "anonymous"}</code>
        </Field>
        <Field label="Client IP / UA">
          <code className="font-mono text-xs text-[--muted]">{row.client_ip || "—"} · {row.user_agent || "—"}</code>
        </Field>
        <Field label="Friendly message shown to user">
          <span className="text-xs">{row.friendly_message}</span>
        </Field>
        <Field label="Internal message">
          <pre className="font-mono text-xs whitespace-pre-wrap break-words bg-[--paper] border border-[--line] p-3">{row.error_message}</pre>
        </Field>
        <Field label="Traceback">
          <pre className="font-mono text-[11px] whitespace-pre-wrap break-words bg-[--paper] border border-[--line] p-3 max-h-[40vh] overflow-y-auto" data-testid="error-detail-traceback">
            {row.traceback}
          </pre>
        </Field>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="mb-4">
      <div className="overline text-[--muted] mb-1">{label}</div>
      {children}
    </div>
  );
}

function ConfirmClearModal({ onCancel, onConfirm, busy }) {
  return (
    <div className="fixed inset-0 bg-[--ink]/60 backdrop-blur-sm grid place-items-center z-50 p-4" onClick={onCancel}>
      <div
        className="bg-white hard-border max-w-md w-full p-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="errors-clear-confirm-modal"
      >
        <div className="overline text-[--muted] mb-2">CONFIRM CLEAR</div>
        <h2 className="font-display font-black text-2xl tracking-tighter inline-flex items-center gap-2">
          <Warning weight="duotone" size={28} className="text-amber-600" /> Wipe error log?
        </h2>
        <p className="text-sm text-[--muted] mt-3">
          This deletes every row in the <code className="font-mono">error_log</code> collection.
          New errors will start logging immediately afterwards.
        </p>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onCancel} disabled={busy} data-testid="errors-clear-cancel" className="border border-[--ink] px-4 py-2 text-xs font-bold hover:bg-[--paper] disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={busy}
            data-testid="errors-clear-confirm"
            className="bg-red-600 text-white px-4 py-2 text-xs font-bold hover:bg-red-700 disabled:opacity-50 inline-flex items-center gap-2"
          >
            <Trash weight="bold" size={14} /> {busy ? "Clearing…" : "Clear all"}
          </button>
        </div>
      </div>
    </div>
  );
}
